import * as React from 'react';
import { Empty, Row, Spin, Typography, List } from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getOrgLoading,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { TrackingCategory } from 'store/organisation/models';
import { StyledCard } from 'components/Custom/card';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import { useFetchTrackingCategories } from 'hooks/useFetchTrackingCategories';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styled from 'styled-components';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';
import { TransactionEditDrawerLinesScroll } from './transactions-tab/styles';
const { Text } = Typography;

/** Space reserved above the scrollable list (tabs, category tabs, card header, padding). */
const TRACKING_CATEGORIES_LIST_VIEWPORT_OFFSET_WIDE = 260;
const TRACKING_CATEGORIES_LIST_VIEWPORT_OFFSET_NARROW = 300;
const TRACKING_CATEGORIES_LIST_MIN_HEIGHT = 320;

const TrackingCategoryTabsBar = styled.div`
  width: 100%;
  max-width: 100%;
  margin-top: -5px;
  margin-bottom: 20px;

  .ant-tabs {
    width: 100%;
    max-width: 100%;
    margin-bottom: 0;
  }

  .ant-tabs-nav-wrap {
    padding: 0 !important;
  }

  .ant-tabs-nav {
    position: static !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    z-index: auto !important;
    background: transparent !important;

    &::before {
      border-color: var(--color-border, #dbeae3);
    }
  }

  .ant-tabs-content-holder {
    display: none;
  }
`;

const TrackingCategoryOptions = styled.div`
  width: 100%;
  max-width: 100%;
  margin-top: 12px;

  .ant-list {
    width: 100%;
  }

  .ant-list-items {
    display: grid;
    gap: 8px;
  }

  .ant-list-item {
    padding: 10px 12px;
    margin: 0;
    border-radius: 10px;
    border: 1px solid rgba(4, 42, 42, 0.06);
    background: rgba(255, 255, 255, 0.9);
    transition: background 120ms ease, border-color 120ms ease;
  }

  .ant-list-item:hover {
    background: var(--color-surface-raised, #ffffff);
    border-color: rgba(4, 42, 42, 0.12);
  }
`;

const TrackingCategoryOptionsEmpty = styled.div`
  padding: 24px 0 8px;
`;

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;

const toTitleCase = (value?: string) => {
  if (!value) return '-';
  return value
    .trim()
    .split(/\s+/)
    .map((word) =>
      word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : ''
    )
    .join(' ');
};

/** Narrow layout: match chart-of-accounts / tax-rates reference tabs. */
const TC_NARROW_MAIN_COLUMN_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  marginRight: '24px',
  boxSizing: 'border-box',
  paddingRight: 'clamp(72px, 16vw, 40px)',
};

const TC_NARROW_INNER_STYLE: React.CSSProperties = {
  padding: '0 40px',
  width: '100%',
  maxWidth: 1250,
  margin: '0 auto',
  boxSizing: 'border-box',
  minWidth: 0,
};

interface TrackingCategoriesTabProps {
  organisation?: any;
}

export const TrackingCategoriesTab: React.FC<TrackingCategoriesTabProps> = ({
  organisation,
}) => {
  const { t } = useTranslation();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const tcWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;

  const organisationId = organisation?.uuid ?? '';
  const orgLoading = useSelector(getOrgLoading);
  const organisations = useSelector(getOrgOrganisations);

  const storeOrganisation = React.useMemo(
    () => organisations?.find((o) => o.uuid === organisationId),
    [organisations, organisationId]
  );

  useFetchTrackingCategories(organisationId, storeOrganisation, true);

  const trackingCategories: TrackingCategory[] = React.useMemo(
    () => storeOrganisation?.trackingCategories ?? [],
    [storeOrganisation?.trackingCategories]
  );

  const categoriesLoaded = storeOrganisation?.trackingCategories !== undefined;

  const tcLoading = !!organisationId && (!categoriesLoaded || orgLoading);

  const [activeCategoryId, setActiveCategoryId] = React.useState<string | null>(
    null
  );
  React.useEffect(() => {
    if (!trackingCategories.length) {
      setActiveCategoryId(null);
      return;
    }
    const stillValid = trackingCategories.some((tc) => tc.id === activeCategoryId);
    if (!stillValid) {
      setActiveCategoryId(trackingCategories[0].id);
    }
  }, [trackingCategories, activeCategoryId]);

  const trackingCategoryTabItems = React.useMemo(
    () =>
      trackingCategories.map((tc) => ({
        key: tc.id,
        label: toTitleCase(tc.name) || tc.name || '-',
      })),
    [trackingCategories]
  );

  const activeCategory = React.useMemo(
    () =>
      trackingCategories.find((tc) => tc.id === activeCategoryId) ??
      trackingCategories[0] ??
      null,
    [trackingCategories, activeCategoryId]
  );

  const activeOptions = activeCategory?.options ?? [];

  const listMaxHeight = React.useMemo(
    () =>
      Math.max(
        TRACKING_CATEGORIES_LIST_MIN_HEIGHT,
        windowHeight -
          (tcWideLayout
            ? TRACKING_CATEGORIES_LIST_VIEWPORT_OFFSET_WIDE
            : TRACKING_CATEGORIES_LIST_VIEWPORT_OFFSET_NARROW)
      ),
    [windowHeight, tcWideLayout]
  );

  const tcTableCard = (
    <StyledCard
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: tcWideLayout ? 600 : 0,
        ...(!tcWideLayout ? { overflowX: 'auto' as const } : {}),
      }}
    >
      <div
        style={{
          padding: '5px',
          paddingLeft: '5px',
          paddingTop: '5px',
          minWidth: tcWideLayout ? 500 : 0,
          margin: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: '32px',
            }}
          >
            <CardTitleLargeSpan style={{ fontSize: 20, lineHeight: '32px' }}>
              {t('organisation.reference-tracking-categories')}
            </CardTitleLargeSpan>
          
          </div>
          
        </div>
        <Spin spinning={tcLoading}>
          {trackingCategoryTabItems.length > 0 ? (
            <>
              <TrackingCategoryTabsBar>
                <Row
                  style={{
                    marginTop: -5,
                    marginBottom: 20,
                    position: 'relative',
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'hidden',
                  }}
                >
                  <ScopeTabs
                    className="tracking-category-tabs"
                    activeKey={
                      activeCategoryId ?? trackingCategoryTabItems[0].key
                    }
                    onChange={setActiveCategoryId}
                    items={trackingCategoryTabItems}
                  />
                </Row>
              </TrackingCategoryTabsBar>
              <TrackingCategoryOptions>
                {activeOptions.length > 0 ? (
                  <TransactionEditDrawerLinesScroll
                    style={{ maxHeight: listMaxHeight }}
                  >
                    <List
                      size="small"
                      dataSource={activeOptions}
                      renderItem={(opt) => (
                        <List.Item key={opt.id}>
                          <Text style={{ color: 'var(--color-text-dark, #042a2a)' }}>
                            {toTitleCase(opt.name)}
                          </Text>
                        </List.Item>
                      )}
                    />
                  </TransactionEditDrawerLinesScroll>
                ) : (
                  <TrackingCategoryOptionsEmpty>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>
                          {t('global.no-data-available', 'No data available')}
                        </Text>
                      }
                    />
                  </TrackingCategoryOptionsEmpty>
                )}
              </TrackingCategoryOptions>
            </>
          ) : !tcLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>
                    {t('global.no-data-available', 'No data available')}
                  </Text>
                }
              />
            </div>
          ) : null}
        </Spin>
      </div>
    </StyledCard>
  );

  return (
    <div
      className="entity-tracking-categories-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`tracking-categories-tab-wrapper${!tcWideLayout ? ' tracking-categories-tab-wrapper--narrow' : ''
          }`}
      >
        {tcWideLayout ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 20,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: '1 1 auto',
                minWidth: 600,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {tcTableCard}
            </div>
            <div
              style={{
                width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
                flexShrink: 0,
              }}
            />
          </div>
        ) : (
          <div className="entity-overview-tab entity-details-narrow-side-shell">
            <div style={TC_NARROW_MAIN_COLUMN_STYLE}>
              <div style={TC_NARROW_INNER_STYLE}>{tcTableCard}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
